import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { useSidebar } from '../../../context/SidebarContext';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';

import {
  HeaderRow,
  PageWrapper,
  PaginationFooter,
  StatusTag,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableContainer,
} from '../List/styles';
import CustomTableHeader from '../../../components/CustomHeader';
import CustomPagination from '../../../components/CustomPagination';
import { Box, TableRow, Typography, Divider } from '@mui/material';
import DetailModal from '../../../components/DetailModal'

import {
  fetchOngoingShifts,
  selectOngoingShifts,
  selectOngoingShiftsLoading,
  selectOngoingShiftsError,
  selectOngoingShiftsCount,
  IOngoingShift,
  IShiftAssignment,
} from '../../../modules/shift';

import { selectFacilityId } from '../../../modules/facilityProfile';
import WorkerAssigned from './components/WorkerAssigned';
import ShiftDetails from '../List/components/ShiftDetails';
import LogException from './components/LogException';

type Order = 'asc' | 'desc';

interface Column {
  id: keyof IShiftAssignment | string;
  label: string;
  numeric?: boolean;
  disableSorting?: boolean;
}

const COLUMN_MAPPING: { [key: string]: keyof IShiftAssignment | string } = {
  'Shift No': 'shift',
  "Worker's Name": 'worker',
  Position: 'position_role',
  'Start Time': 'start_date_time',
  'End Time': 'end_date_time',
  'Clock In': 'clock_in_time',
  'Clock Out': 'clock_out_time',
  Status: 'status',
};

const ONGOING_SHIFT_COLUMNS: Column[] = Object.entries(COLUMN_MAPPING).map(
  ([label, id]) => ({
    id: id,
    label: label,
    numeric: id === 'shift',
  }),
);

const OngoingShifts: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setIsShow } = useBreadcrumb();
  useEffect(() => {
    setActivePage('shift_monitoring');
    setIsShow(false);
  }, [setActivePage, setIsShow]);

  const dispatch = useDispatch<any>();
  const ongoingShifts = useSelector(selectOngoingShifts);
  const ongoingShiftsLoading = useSelector(selectOngoingShiftsLoading);
  const ongoingShiftsError = useSelector(selectOngoingShiftsError);
  const ongoingShiftsCount = useSelector(selectOngoingShiftsCount);
  const facilityId = useSelector(selectFacilityId);

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof IShiftAssignment | string>(
    'shift',
  );

  const [shiftDetails, setShiftDetails] = useState<any | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleOpenDrawer = () => setDrawerOpen(true);
  const handleCloseDrawer = () => setDrawerOpen(false);

  useEffect(() => {
    if (facilityId) {
      dispatch(
        fetchOngoingShifts({
          facilityId,
          page,
          pageSize: rowsPerPage,
        }),
      );
    }
  }, [dispatch, facilityId, page, rowsPerPage]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const propertyKey = property as keyof IShiftAssignment | string;

    const columnDef = ONGOING_SHIFT_COLUMNS.find((col) => col.id === property);
    if (columnDef?.disableSorting) {
      return;
    }

    const isAsc = orderBy === propertyKey && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(propertyKey);
  };

  const getStatus = (status: string, clockInTime: string | null, clockOutTime: string | null, lateArrival: boolean) => {
    if (status === 'accepted' && !clockInTime) return 'Absent';
    if (clockInTime && lateArrival) return 'Clocked In (Late)';
    if (clockInTime && !clockOutTime) return 'Active';
    if (clockOutTime) return 'Completed';

    return status;
  };

  const getShiftDataFromAssignments = (shifts: IOngoingShift[]) => {
    const shiftData: any[] = [];
    if (!Array.isArray(shifts)) return [];
    shifts.forEach((shift) => {
      const assignments = Array.isArray(shift.shift_assignment) ? shift.shift_assignment : [];
      assignments.forEach((assignment) => {
        shiftData.push({
          ...assignment,
          start_date_time: shift.start_date_time,
          end_date_time: shift.end_date_time,
          position_role: shift.position_role,
          shift_id: shift.shift_id,
          attendance_manager: shift.attendance_manager,
          experience_required: shift.experience_required,
          workers_required: shift.workers_required,
          assigned_count: assignments.length,
        });
      });
    });
    return shiftData;
  };

  const onClickRow = (shift: any) => {
    setShiftDetails(shift);
    handleOpenDrawer();
  };

  return (
    <PageWrapper>
      <Box sx={{ width: '100%', padding: 3 }}>
        <HeaderRow>
          <Typography
            variant="h5"
            style={{ fontWeight: 'bold', color: '#222' }}
          >
            All Live Shifts
          </Typography>
          <Typography
            variant="body2"
          >
            Last updated:{' '}
            {dayjs().format('DD-MM-YYYY hh:mm A')}
          </Typography>
        </HeaderRow>
        <StyledTableContainer>
          <StyledTable stickyHeader>
            <CustomTableHeader
              columns={ONGOING_SHIFT_COLUMNS}
              order={order}
              orderBy={orderBy as string}
              onRequestSort={handleRequestSort}
            />
            <StyledTableBody>
              {ongoingShiftsLoading ? (
                <TableRow>
                  <StyledTableCell
                    colSpan={ONGOING_SHIFT_COLUMNS.length}
                    align="center"
                  >
                    Loading...
                  </StyledTableCell>
                </TableRow>
              ) : ongoingShiftsError ||
                !ongoingShifts ||
                ongoingShifts.length === 0 ? (
                <TableRow>
                  <StyledTableCell
                    colSpan={ONGOING_SHIFT_COLUMNS.length}
                    align="center"
                  >
                    No data available
                  </StyledTableCell>
                </TableRow>
              ) : (
                getShiftDataFromAssignments(ongoingShifts).map((assignment) => (
                  <TableRow key={assignment.assignment_id} hover onClick={() => onClickRow(assignment)} sx={{ cursor: 'pointer' }}>
                    <StyledTableCell>{assignment.shift}</StyledTableCell>
                    <StyledTableCell>
                      {assignment.worker.user.first_name}{' '}
                      {assignment.worker.user.last_name}
                    </StyledTableCell>
                    <StyledTableCell>{assignment.position_role}</StyledTableCell>
                    <StyledTableCell>
                      {dayjs(assignment.start_date_time).format('HH:mm')}
                    </StyledTableCell>
                    <StyledTableCell>
                      {dayjs(assignment.end_date_time).format('HH:mm')}
                    </StyledTableCell>
                    <StyledTableCell>
                      {(assignment.clock_in_time) ? dayjs(assignment.clock_in_time).format('HH:mm') : '-'}
                    </StyledTableCell>
                    <StyledTableCell>
                      {(assignment.clock_out_time) ? dayjs(assignment.clock_out_time).format('HH:mm') : '-'}
                    </StyledTableCell>
                    <StyledTableCell>
                      <StatusTag status={getStatus(
                        assignment.status,
                        assignment.clock_in_time,
                        assignment.clock_out_time,
                        assignment.late_arrival
                      )}>
                        {getStatus(
                          assignment.status,
                          assignment.clock_in_time,
                          assignment.clock_out_time,
                          assignment.late_arrival
                        )}
                      </StatusTag>
                    </StyledTableCell>
                  </TableRow>
                ))
              )}
            </StyledTableBody>
          </StyledTable>
        </StyledTableContainer>
        <PaginationFooter>
          <Box>
            Showing {getShiftDataFromAssignments(ongoingShifts)?.length || 0} of{' '}
            {ongoingShiftsCount} results
          </Box>
          <CustomPagination
            currentPage={page}
            totalPages={Math.ceil(ongoingShiftsCount / rowsPerPage)}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </PaginationFooter>
      </Box>
      {shiftDetails &&
        <DetailModal
          open={drawerOpen}
          onClose={handleCloseDrawer}
          shiftId={shiftDetails.shift_id}
          shiftStatus={getStatus(
            shiftDetails.status,
            shiftDetails.clock_in_time,
            shiftDetails.clock_out_time,
            shiftDetails.late_arrival
          )}
        >
          <WorkerAssigned
            workerName={shiftDetails.worker.user.first_name + ' ' + shiftDetails.worker.user.last_name}
            workerRole={shiftDetails.position_role}
            workerId={shiftDetails.worker.worker_id}
            avatarSrc={shiftDetails.worker.user.profile_picture || ''}
          />
          <Divider variant="middle" />
          <ShiftDetails
            date={dayjs(shiftDetails.start_date_time).format('DD/MM/YYYY')}
            position={shiftDetails.position_role}
            startTime={dayjs(shiftDetails.start_date_time).format('HH:mm')}
            endTime={dayjs(shiftDetails.end_date_time).format('HH:mm')}
            numberOfWorkers={`${shiftDetails.assigned_count}/${shiftDetails.workers_required}`}
            experience={shiftDetails.experience_required || 0}
            appointedMember={shiftDetails.attendance_manager.first_name + ' ' + shiftDetails.attendance_manager.last_name || '-'}
            clockInTime={(shiftDetails.clock_in_time) ? dayjs(shiftDetails.clock_in_time).format('HH:mm') : '-'}
            clockOutTime={(shiftDetails.clock_out_time) ? dayjs(shiftDetails.clock_out_time).format('HH:mm') : '-'}
            status={shiftDetails.status}
            shiftEndDateTime={shiftDetails.end_date_time}
          />
          <Divider variant="middle" />
          <LogException
            isAbsent={!shiftDetails.clock_in_time && shiftDetails.status === 'accepted'}
            isLateArrival={shiftDetails.late_arrival && shiftDetails.clock_in_time}
          />
        </DetailModal>
      }
    </PageWrapper>
  );
};

export default OngoingShifts;